import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useCashbackList } from '../../../hooks/useCashbackList';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import PaymentRowCard from '../../../components/payments-v2/PaymentRowCard';
import CommissionBreakdownPanel from '../../../components/payments-v2/CommissionBreakdownPanel';

/**
 * Admin V2 Cashback — Liste cashback_records groupés par période.
 * Route : /admin/cashback (V2Switcher) + /admin/v2/cashback.
 * Feature : pages.cashback.management.
 *
 * Pattern identique aux payments : PaymentRowCard + CommissionBreakdownPanel.
 * Filtre status optionnel.
 */
const STATUS_OPTIONS = [
  { id: '',         label: 'All' },
  { id: 'pending',  label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'paid',     label: 'Paid' },
  { id: 'rejected', label: 'Rejected' },
];

const AdminV2Cashback = ({ defaultStatus = null }) => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [statusFilter, setStatusFilter] = useState(defaultStatus || '');

  const { rows, decryptionAvailable, loading, error, reload } = useCashbackList({
    status: statusFilter || null,
  });

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                {defaultStatus === 'paid'
                  ? t('cashbackV2.titlePayments', 'Cashback payments')
                  : t('cashbackV2.title', 'Cashback management')}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {tenantName} · {rows.length} {t('paymentsV2.periods', 'periods')}
              </p>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setExpandedKey(null); }}
              className="px-3 py-2 rounded-lg bg-card border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{t(`paymentsV2.status.${o.id || 'all'}`, o.label)}</option>
              ))}
            </select>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {!decryptionAvailable && (
            <div className="rounded-2xl bg-accent/5 border border-accent/20 p-4 text-xs text-muted-foreground">
              <span className="font-semibold text-accent mr-1">{t('paymentsV2.aesNoteLabel', 'AES heads-up:')}</span>
              {t('cashbackV2.aesNote', 'Cashback amounts could not be decrypted. Check the generic-decrypt-cashback Edge Function.')}
            </div>
          )}

          {loading && rows.length === 0 ? (
            <div className="space-y-3">
              <div className="h-20 skeleton rounded-2xl" />
              <div className="h-20 skeleton rounded-2xl" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('cashbackV2.empty', 'No cashback records yet.')}
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((g) => {
                const isExpanded = expandedKey === g.key;
                const subtitle = `${g.count} ${t('paymentsV2.creators', 'creators')}`;
                return (
                  <PaymentRowCard
                    key={g.key}
                    title={g.period || '—'}
                    subtitle={subtitle}
                    amount={g.totalAmount}
                    status={g.status}
                    expanded={isExpanded}
                    onClick={() => setExpandedKey(isExpanded ? null : g.key)}
                  >
                    <CommissionBreakdownPanel
                      breakdown={g.records.map((r) => ({
                        creator_id: r.creator_id,
                        creator_name: r.creator_name || (r.creator_id ? `${t('paymentsV2.creator', 'Creator')} ${r.creator_id.slice(0, 8)}` : '—'),
                        diamonds: null,
                        amount: r.amount,
                        commission: null,
                        status: r.status,
                      }))}
                      creatorBasePath="/admin/v2/creators"
                      summary={{ totalAmount: g.totalAmount, totalCommission: null, count: g.count }}
                      loading={false}
                    />
                  </PaymentRowCard>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2Cashback;
